import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { manageArticleApi } from "../../../entities/article";
import { Article } from "../../../entities/article";
import { manageKeywordApi, type Keyword } from "../../../entities/keyword";
import { manageProductApi, type Product } from "../../../entities/product";
import { manageAuthorApi, type Author } from "../../../entities/author";
import "./ArticleManagement.scss";

const ArticleManagement: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<string | null>(null);
  // 필터 상태
  const [pendingSearch, setPendingSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKeywordId, setSelectedKeywordId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedAuthorId, setSelectedAuthorId] = useState("");
  const [selectedUxEvaluation, setSelectedUxEvaluation] = useState(""); // uxEvaluation_GOOD | uxEvaluation_BAD
  const [sortBy, setSortBy] = useState<
    "created_at" | "updated_at" | "title_ko" | "title_en"
  >("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  // 선택지 데이터
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [
    currentPage,
    searchTerm,
    selectedKeywordId,
    selectedProductId,
    selectedAuthorId,
    selectedUxEvaluation,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    // 필터 옵션 목록 로드
    (async () => {
      try {
        const [keywordList, productList, authorList] = await Promise.all([
          manageKeywordApi.getKeywordList(),
          manageProductApi.getProductList(),
          manageAuthorApi.getAuthorList(),
        ]);
        setKeywords(keywordList);
        setProducts(productList);
        setAuthors(authorList);
      } catch (e) {
        console.error("Failed to load filter datasets", e);
      }
    })();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);

      // 필터 쿼리 구성
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.append("search", searchTerm.trim());
      if (selectedKeywordId) params.append("keywordId", selectedKeywordId);
      if (selectedProductId) params.append("productId", selectedProductId);
      if (selectedAuthorId) params.append("authorId", selectedAuthorId);
      if (selectedUxEvaluation)
        params.append("uxEvaluation", selectedUxEvaluation);

      const result = await manageArticleApi.getArticleListPaginated(
        currentPage,
        itemsPerPage,
        params.toString(),
        sortBy,
        sortOrder,
      );

      setArticles(result.articles);
      setTotalPages(Math.ceil(result.total / itemsPerPage));
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (id: string) => {
    try {
      await manageArticleApi.deleteArticle(id);
      fetchArticles(); // 서버에서 최신 데이터를 다시 가져옴
      setShowDeleteModal(false);
      setArticleToDelete(null);
    } catch (error) {
      console.error("Failed to delete article:", error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedArticles.map((id) => manageArticleApi.deleteArticle(id)),
      );
      fetchArticles(); // 서버에서 최신 데이터를 다시 가져옴
      setSelectedArticles([]);
    } catch (error) {
      console.error("Failed to delete articles:", error);
    }
  };

  const handleSelectArticle = (id: string) => {
    setSelectedArticles((prev) =>
      prev.includes(id)
        ? prev.filter((articleId) => articleId !== id)
        : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedArticles.length === paginatedArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(paginatedArticles.map((article) => article.id));
    }
  };

  const applySearch = () => {
    setSearchTerm(pendingSearch);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setPendingSearch("");
    setSearchTerm("");
    setSelectedKeywordId("");
    setSelectedProductId("");
    setSelectedAuthorId("");
    setSelectedUxEvaluation("");
    setSortBy("created_at");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  // 백엔드에서 필터링, 정렬, 페이지네이션을 처리하므로 프론트엔드에서는 제거
  const paginatedArticles = articles;

  // 테이블 영역에서만 로딩 표시 (전체 페이지 로딩 제거)

  return (
    <div className="article-management">
      <div className="article-management__header">
        <div className="header-content">
          <h1>게시글 관리</h1>
          <p>게시글을 수정, 삭제할 수 있습니다.</p>
        </div>
        <Link to="/admin/post" className="add-article-btn">
          <span className="btn-icon">✏️</span>새 게시글 작성
        </Link>
      </div>

      {/* 검색 및 필터 영역 */}
      <div className="filters-section">
        <div className="search-box">
          <input
            className="search-input"
            type="text"
            placeholder="제목/내용 검색... (Enter)"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filter-controls">
          <select
            className="category-filter"
            value={selectedKeywordId}
            onChange={(e) => {
              setSelectedKeywordId(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">키워드 전체</option>
            {keywords.map((k) => (
              <option key={k.id} value={k.id}>
                {k.nameKo}
              </option>
            ))}
          </select>

          <select
            className="category-filter"
            value={selectedProductId}
            onChange={(e) => {
              setSelectedProductId(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">프로덕트 전체</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nameKo}
              </option>
            ))}
          </select>

          <select
            className="category-filter"
            value={selectedAuthorId}
            onChange={(e) => {
              setSelectedAuthorId(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">작성자 전체</option>
            {authors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.authorName}
              </option>
            ))}
          </select>

          <select
            className="category-filter"
            value={selectedUxEvaluation}
            onChange={(e) => {
              setSelectedUxEvaluation(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">UX 평가 전체</option>
            <option value="uxEvaluation_GOOD">Good UX</option>
            <option value="uxEvaluation_BAD">Bad UX</option>
          </select>

          <select
            className="sort-select"
            value={`${sortBy}:${sortOrder}`}
            onChange={(e) => {
              const [by, order] = e.target.value.split(":") as [
                typeof sortBy,
                typeof sortOrder,
              ];
              setSortBy(by);
              setSortOrder(order);
              setCurrentPage(1);
            }}
          >
            <option value="created_at:desc">최신순</option>
            <option value="created_at:asc">오래된순</option>
            <option value="title_ko:asc">제목 오름차순</option>
            <option value="title_ko:desc">제목 내림차순</option>
            <option value="updated_at:desc">최근수정순</option>
            <option value="updated_at:asc">오래된수정순</option>
          </select>

          <button className="category-filter" onClick={resetFilters}>
            초기화
          </button>
        </div>
      </div>

      {/* 일괄 작업 */}
      {selectedArticles.length > 0 && (
        <div className="bulk-actions">
          <span className="selected-count">
            {selectedArticles.length}개 선택됨
          </span>
          <button onClick={handleBulkDelete} className="bulk-delete-btn">
            선택한 게시글 삭제
          </button>
        </div>
      )}

      {/* 게시글 테이블 */}
      <div className="articles-table-container">
        {loading && <div className="table-loading-bar" />}
        <table className="articles-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedArticles.length === paginatedArticles.length &&
                    paginatedArticles.length > 0
                  }
                  onChange={handleSelectAll}
                  className="select-all-checkbox"
                />
              </th>
              <th>제목</th>
              <th>카테고리</th>
              <th>키워드</th>
              <th>프로덕트</th>
              <th>작성자</th>
              <th>작성일</th>
              <th>수정일</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="skeleton-row">
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "2rem", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "70%", height: "2.2rem" }}
                      />
                      <div
                        className="skeleton-box"
                        style={{
                          width: "90%",
                          height: "1.8rem",
                          marginTop: "0.8rem",
                        }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "80%", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "80%", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "80%", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "70%", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "60%", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "60%", height: "2rem" }}
                      />
                    </td>
                    <td>
                      <div
                        className="skeleton-box"
                        style={{ width: "100%", height: "2.4rem" }}
                      />
                    </td>
                  </tr>
                ))
              : paginatedArticles.map((article) => (
                  <tr key={article.id} className="article-row">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedArticles.includes(article.id)}
                        onChange={() => handleSelectArticle(article.id)}
                        className="article-checkbox"
                      />
                    </td>
                    <td className="article-title">
                      <div className="title-content">
                        <h3>{article.titleKo || "제목 없음"}</h3>
                        <p className="title-preview">
                          {article.contentKo?.substring(0, 100)}...
                        </p>
                      </div>
                    </td>
                    <td className="article-category">
                      <span className="category-badge">
                        {article.keyword?.categoryNameKo || "카테고리 없음"}
                      </span>
                    </td>
                    <td className="article-keyword">
                      <span className="keyword-badge">
                        {article.keyword?.nameKo || "키워드 없음"}
                      </span>
                    </td>
                    <td className="article-product">
                      <span className="product-badge">
                        {article.product?.nameKo || "프로덕트 없음"}
                      </span>
                    </td>
                    <td className="article-author">
                      {article.author?.authorName || "작성자 없음"}
                    </td>
                    <td className="article-date">
                      {article.createdAt
                        ? new Date(article.createdAt).toLocaleDateString(
                            "ko-KR",
                          )
                        : "-"}
                    </td>
                    <td className="article-date">
                      {article.updatedAt
                        ? new Date(article.updatedAt).toLocaleDateString(
                            "ko-KR",
                          )
                        : "-"}
                    </td>
                    <td className="article-actions">
                      <Link
                        to={`/admin/article/modify/${article.id}`}
                        className="action-btn edit-btn"
                      >
                        수정
                      </Link>
                      <button
                        onClick={() => {
                          setArticleToDelete(article.id);
                          setShowDeleteModal(true);
                        }}
                        className="action-btn delete-btn"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>

        {!loading && paginatedArticles.length === 0 && (
          <div className="empty-state">
            <p>검색 결과가 없습니다.</p>
            <Link to="/admin/post" className="add-first-article-btn">
              첫 게시글 작성하기
            </Link>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      <div className="article-management__pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          이전
        </button>
        <span>
          {currentPage} / {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          다음
        </button>
      </div>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>게시글 삭제</h3>
            <p>정말로 이 게시글을 삭제하시겠습니까?</p>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setArticleToDelete(null);
                }}
                className="cancel-btn"
              >
                취소
              </button>
              <button
                onClick={() =>
                  articleToDelete && handleDeleteArticle(articleToDelete)
                }
                className="confirm-btn"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleManagement;
